import axios from "axios";
import {
  API_BASE_URL,
  API_REQUEST_TIMEOUT_MS,
  SIGNUP_REQUEST_TIMEOUT_MS,
  SIGNUP_MAX_RETRIES,
  getApiUnavailableMessage,
  isApiNetworkError,
  isAxiosTimeoutError,
} from "./deployConfig";

export { API_BASE_URL } from "./backend";
export const API = `${API_BASE_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: API_REQUEST_TIMEOUT_MS,
});

export {
  isApiNetworkError,
  getApiUnavailableMessage,
  SIGNUP_REQUEST_TIMEOUT_MS,
  SIGNUP_MAX_RETRIES,
  isAxiosTimeoutError,
} from "./deployConfig";

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("dsite_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const isFormData =
    typeof FormData !== "undefined" &&
    (config.data instanceof FormData || config.data?.constructor?.name === "FormData");
  if (isFormData) {
    // multipart boundary는 브라우저가 자동 설정 (Content-Type을 붙이면 업로드 실패)
    if (typeof config.headers?.delete === "function") {
      config.headers.delete("Content-Type");
      config.headers.delete("content-type");
    } else if (config.headers) {
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
    }
  } else if (config.data !== undefined && config.data !== null && !config.headers["Content-Type"]) {
    config.headers["Content-Type"] = "application/json";
  }
  return config;
});

/** FastAPI validation / HTTP 에러 메시지를 사용자용 문자열로 변환 */
function formatApiDetail(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || item?.message || JSON.stringify(item)).join(", ");
  }
  if (detail && typeof detail === "object") {
    return detail.message || JSON.stringify(detail);
  }
  return err?.message || fallback;
}

const SIGNUP_NETWORK_ERROR_MESSAGE =
  "회원가입에 실패했습니다. 네트워크 상태를 확인 후 다시 시도해 주세요.";
const SIGNUP_TIMEOUT_ERROR_MESSAGE = "회원가입 요청 시간이 초과되었습니다. 다시 시도해 주세요.";

export function formatApiError(err, fallback = "요청 실패") {
  if (isApiNetworkError(err)) return getApiUnavailableMessage();
  return formatApiDetail(err, fallback);
}

/** Signup-only user messages (no static-deploy "서버 연결 준비 중" copy). */
export function formatSignupError(err, fallback = "회원가입 중 오류가 발생했습니다.") {
  if (isAxiosTimeoutError(err)) return SIGNUP_TIMEOUT_ERROR_MESSAGE;
  if (isApiNetworkError(err)) return SIGNUP_NETWORK_ERROR_MESSAGE;
  return formatApiDetail(err, fallback);
}

function normalizeFetchFailure(err) {
  if (err?.response) return err;
  if (err?.name === "AbortError") {
    const timeoutErr = new Error(`timeout of ${SIGNUP_REQUEST_TIMEOUT_MS}ms exceeded`);
    timeoutErr.code = "ECONNABORTED";
    return timeoutErr;
  }
  return err;
}

/** Signup POST via fetch — signup-specific errors, no warm/retry loop. */
export async function postCustomerSignup(body, { timeoutMs = SIGNUP_REQUEST_TIMEOUT_MS } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${API}/customer/signup`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    let data = {};
    try {
      data = await res.json();
    } catch {
      data = {};
    }
    if (!res.ok) {
      const httpErr = new Error(formatApiDetail({ response: { data } }, "회원가입 중 오류가 발생했습니다."));
      httpErr.response = { status: res.status, data };
      throw httpErr;
    }
    return { data };
  } catch (err) {
    throw normalizeFetchFailure(err);
  } finally {
    clearTimeout(timer);
  }
}

/** 미디어 파일 업로드 (SEO OG · 파비콘 · 미디어 관리 공통) — fetch 사용으로 multipart 안정화 */
export async function uploadMediaFile(file, category = "image") {
  const form = new FormData();
  form.append("file", file);
  form.append("category", category);
  const token = localStorage.getItem("dsite_token");
  const res = await fetch(`${API}/admin/media/upload`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  let data = {};
  try {
    data = await res.json();
  } catch {
    data = {};
  }
  if (!res.ok) {
    const err = new Error(formatApiError({ response: { data } }, "업로드 실패"));
    err.response = { status: res.status, data };
    throw err;
  }
  return data;
}

export async function assignMediaSlot(slot, url = "") {
  const { data } = await api.post("/admin/media/assign", { slot, url: url || "" });
  return data;
}

/** SEO OG / 카카오 공유 이미지 저장 (전용 API → CMS fallback) */
export async function assignSeoOgImage(url = "") {
  try {
    const { data } = await api.put("/admin/seo/og-image", { url: url || "" });
    return data;
  } catch (err) {
    if (err?.response?.status === 404) {
      const { data } = await api.put("/admin/cms", { media_og_image: url || "" });
      return data;
    }
    throw err;
  }
}

export async function submitInquiry(payload) {
  const body = {
    name: payload.name,
    email: payload.email,
    phone: payload.phone || undefined,
    company: payload.company || undefined,
    service_type: payload.project_type || payload.service_type || undefined,
    budget: payload.budget || undefined,
    message: payload.message,
  };
  const { data } = await api.post("/inquiries", body);
  return data;
}
